"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCasePaymentStatusTags = getCasePaymentStatusTags;
exports.splitCasePaymentTotals = splitCasePaymentTotals;
exports.resolveCaseMilestoneInvoice = resolveCaseMilestoneInvoice;
exports.applyCaseDrawdownToEscrowAmounts = applyCaseDrawdownToEscrowAmounts;
exports.getCaseMilestoneEscrowAmounts = getCaseMilestoneEscrowAmounts;
exports.getCaseMilestoneHeldAfterDrawdown = getCaseMilestoneHeldAfterDrawdown;
exports.sumCaseMilestoneEscrowTotals = sumCaseMilestoneEscrowTotals;
exports.calculateCasePartialReleaseAmounts = calculateCasePartialReleaseAmounts;
exports.resolveCasePayInNotificationAmounts = resolveCasePayInNotificationAmounts;
const case_workflow_helpers_js_1 = require("./case-workflow.helpers.js");
const paid_off_platform_js_1 = require("../invoice/paid-off-platform.js");
function getCasePaymentStatusTags(invoice, options) {
    if (!invoice) {
        return { tags: [] };
    }
    const milestoneAmount = options?.milestoneAmount ?? 0;
    // Before the CASE-invoice gate: paid-off-platform rows often lack instantReleasePercent
    // (ops mark does not stamp it), but settlement UX must still show full released / $0 held.
    if ((0, paid_off_platform_js_1.isPaidOffPlatform)(invoice)) {
        return { tags: [paid_off_platform_js_1.PAID_OFF_PLATFORM_LABEL], releasedAmount: milestoneAmount, heldAmount: 0 };
    }
    if (!(0, case_workflow_helpers_js_1.isCaseWorkflowInvoice)(invoice)) {
        return { tags: [] };
    }
    const partial = invoice.paymentMetadata?.partial_release;
    const isPaid = invoice.status === "paid";
    const isApproved = options?.paymentRequestStatus === "approved";
    if (isApproved) {
        return { tags: ["Released from Escrow"], releasedAmount: milestoneAmount, heldAmount: 0 };
    }
    if (!isPaid) {
        return { tags: ["Unpaid"] };
    }
    const releasePercent = Number(invoice.instantReleasePercent ?? 0);
    if (releasePercent === 0) {
        const held = partial?.heldInEscrow ?? milestoneAmount;
        return { tags: ["100% In Escrow"], releasedAmount: 0, heldAmount: held };
    }
    if (partial?.releasedAtPayment != null && partial.releasedAtPayment > 0 && (partial.heldInEscrow ?? 0) > 0) {
        if (releasePercent === 50 && milestoneAmount > 0) {
            const { releasedAmount, heldAmount } = resolveCaseFiftyFiftyEscrowAmounts(milestoneAmount);
            return {
                tags: ["50% Released", "50% In Escrow"],
                releasedAmount,
                heldAmount,
            };
        }
        return {
            tags: ["50% Released", "50% In Escrow"],
            releasedAmount: partial.releasedAtPayment,
            heldAmount: partial.heldInEscrow,
        };
    }
    if (releasePercent === 50) {
        const { releasedAmount, heldAmount } = resolveCaseFiftyFiftyEscrowAmounts(milestoneAmount);
        return {
            tags: ["50% Released", "50% In Escrow"],
            releasedAmount,
            heldAmount,
        };
    }
    return { tags: ["In Escrow"], heldAmount: milestoneAmount };
}
/** Split a currency amount by release percent using integer cents (released + held === total). */
function splitCasePaymentTotals(totalAmount, releasePercent) {
    const totalCents = Math.round(Math.max(0, totalAmount) * 100);
    if (releasePercent <= 0) {
        return { totalReleased: 0, totalHeld: totalCents / 100 };
    }
    if (releasePercent >= 100) {
        return { totalReleased: totalCents / 100, totalHeld: 0 };
    }
    const releasedCents = Math.round((totalCents * releasePercent) / 100);
    return {
        totalReleased: releasedCents / 100,
        totalHeld: (totalCents - releasedCents) / 100,
    };
}
function resolveCaseFiftyFiftyEscrowAmounts(milestoneAmount) {
    const { totalReleased, totalHeld } = splitCasePaymentTotals(milestoneAmount, 50);
    return { releasedAmount: totalReleased, heldAmount: totalHeld };
}
function resolveCaseMilestoneInvoice(milestone, invoices) {
    let resolved;
    if (milestone.invoice?.id && invoices?.length) {
        resolved = invoices.find((inv) => inv.id === milestone.invoice.id) ?? undefined;
    }
    if (!resolved && milestone.invoice) {
        resolved = milestone.invoice;
    }
    if (!resolved && milestone.projectInvoiceId && invoices?.length) {
        resolved = invoices.find((inv) => inv.id === milestone.projectInvoiceId);
    }
    if (!resolved) {
        return milestone.invoice;
    }
    // Prefer list row for fresh status/metadata, but keep CASE markers from the milestone-shaped
    // invoice when the persisted invoice never stamped instantReleasePercent.
    if (milestone.invoice && resolved !== milestone.invoice) {
        return {
            ...resolved,
            instantReleasePercent: resolved.instantReleasePercent ?? milestone.invoice.instantReleasePercent,
            paymentMetadata: resolved.paymentMetadata ?? milestone.invoice.paymentMetadata,
        };
    }
    return resolved;
}
function applyCaseDrawdownToEscrowAmounts(released, held, drawdownAmount) {
    const drawdown = Math.max(0, Number(drawdownAmount ?? 0));
    if (drawdown <= 0) {
        return { released, held };
    }
    const deducted = Math.min(drawdown, held);
    return {
        released: Math.round((released + deducted) * 100) / 100,
        held: Math.round((held - deducted) * 100) / 100,
    };
}
function getCaseMilestoneEscrowAmounts(milestone, invoices) {
    const invoice = resolveCaseMilestoneInvoice(milestone, invoices);
    const result = getCasePaymentStatusTags(invoice, {
        paymentRequestStatus: milestone.paymentRequestStatus,
        milestoneAmount: milestone.budgetAmount ?? 0,
    });
    let released = result.releasedAmount ?? 0;
    let held = result.heldAmount ?? 0;
    if (milestone.paymentRequestStatus !== "approved") {
        ({ released, held } = applyCaseDrawdownToEscrowAmounts(released, held, milestone.drawdownAmount));
    }
    return { released, held };
}
/** Remaining escrow for approval after drawdown (physical balance still in escrow wallet). */
function getCaseMilestoneHeldAfterDrawdown(milestone, invoices) {
    return getCaseMilestoneEscrowAmounts(milestone, invoices).held;
}
function sumCaseMilestoneEscrowTotals(milestones, invoices) {
    let totalReleased = 0;
    let totalHeld = 0;
    for (const milestone of milestones ?? []) {
        const { released, held } = getCaseMilestoneEscrowAmounts(milestone, invoices);
        totalReleased += released;
        totalHeld += held;
    }
    return {
        totalReleased: Math.round(totalReleased * 100) / 100,
        totalHeld: Math.round(totalHeld * 100) / 100,
    };
}
function calculateCasePartialReleaseAmounts(milestoneAmountCaptured, businessHandshakeFee, releasePercent) {
    const captured = Math.round(Math.max(0, milestoneAmountCaptured) * 100) / 100;
    const businessFee = Math.round(Math.max(0, businessHandshakeFee) * 100) / 100;
    const { totalReleased, totalHeld } = splitCasePaymentTotals(captured, releasePercent);
    const totalBusinessRevenue = Math.max(0, Math.round((captured - businessFee) * 100) / 100);
    if (captured <= 0 || releasePercent <= 0) {
        return {
            businessRevenueReleased: 0,
            businessRevenueHeld: totalBusinessRevenue,
            feeReleased: 0,
            feeHeld: businessFee,
            totalReleased: 0,
            totalHeld,
        };
    }
    const businessShare = totalBusinessRevenue / captured;
    const businessRevenueReleased = Math.round(totalReleased * businessShare * 100) / 100;
    const feeReleased = Math.round((totalReleased - businessRevenueReleased) * 100) / 100;
    const businessRevenueHeld = Math.round((totalBusinessRevenue - businessRevenueReleased) * 100) / 100;
    const feeHeld = Math.round((businessFee - feeReleased) * 100) / 100;
    return {
        businessRevenueReleased,
        businessRevenueHeld,
        feeReleased,
        feeHeld,
        totalReleased,
        totalHeld,
    };
}
/** Slack notification amounts use gross client-paid totals so ops can match the project UI. */
function resolveCasePayInNotificationAmounts(input) {
    if (input.releasePercent <= 0) {
        return {
            clientPaidAmount: input.clientPaidAmount,
            releasedAtPayment: 0,
            heldInEscrow: input.heldInEscrowGross,
        };
    }
    return {
        clientPaidAmount: input.clientPaidAmount,
        releasedAtPayment: input.releasedAtPaymentGross,
        heldInEscrow: input.heldInEscrowGross,
    };
}
